/**
 * Coast's one link format (docs/views.md#5-navigation-sections-and-links): `coast:<chat>/<view>#<section>?<key>=<value>&…`,
 * such as `coast:k49csx5a/design#suggestion?id=sg_7`, where the chat and the view are ids, and the section and its
 * parameters are optional, so `coast:<chat>` names a chat and `coast:<chat>/<view>` a view in it. The server's tools, the
 * chats, the view objects, and the runtime read links with `parseLink` and write them with `writeLink`, and keep only
 * this form. For one release of habit `parseLink` also reads the forms before it: `/c/<chat>/<view>#…`, the same on
 * coast.ws or another origin given, and `<view>#<section>` in a chat's own context.
 */

/** What a link names: a chat, a view in it, and a section of the view with its parameters. */
export interface CoastLink {
  chat: string;
  view?: string;
  section?: string;
  params?: Record<string, string>;
}

/** A chat's id, or a view's in a chat. */
const ID = /^[A-Za-z0-9_-]{1,64}$/;
/** A section's name, as a manifest's `sections` names it. */
export const SECTION_NAME = /^[a-z][a-z0-9_-]{0,39}$/;
/** Coast's front door, whose `/c/` links named a chat's view before the `coast:` form. */
const COAST_ORIGINS = ["https://coast.ws", "https://www.coast.ws"];
/** The longest link kept: a notification carries it (the push relay holds 320 characters). */
export const LINK_CHARS = 300;

/** Where a link is read: in a chat, for `<view>#<section>`, and the origins whose `/c/` paths are Coast's besides coast.ws. */
export interface LinkContext {
  chat?: string | null;
  origins?: readonly (string | null | undefined)[];
}

/** The part after a chat's id: `<chat>[/<view>][#<section>[?<query>]]`, taken apart; null when it isn't one. */
function partsOf(rest: string): CoastLink | null {
  const hash = rest.indexOf("#");
  const head = hash < 0 ? rest : rest.slice(0, hash);
  const fragment = hash < 0 ? null : rest.slice(hash + 1);
  const ids = head.split("/");
  if (ids.length > 2 || !ids.every((id) => ID.test(id))) return null;
  const link: CoastLink = { chat: ids[0]! };
  if (ids[1]) link.view = ids[1];
  if (fragment === null || fragment === "") return link;
  if (!link.view) return null;
  const query = fragment.indexOf("?");
  const section = query < 0 ? fragment : fragment.slice(0, query);
  if (!SECTION_NAME.test(section)) return null;
  link.section = section;
  if (query >= 0) {
    const params: Record<string, string> = {};
    for (const [key, value] of new URLSearchParams(fragment.slice(query + 1))) if (key) params[key] = value;
    if (Object.keys(params).length) link.params = params;
  }
  return link;
}

/** An origin, `https://host[:port]`, or null for what isn't a URL. */
function originOf(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return /^https?:$/.test(url.protocol) ? url.origin : null;
  } catch {
    return null;
  }
}

/**
 * The chat, view, and section a link names, from the `coast:` form or one before it (above); null for anything else,
 * such as a link to another website, a view's home, or a bare id outside a chat's context.
 */
export function parseLink(value: unknown, context: LinkContext = {}): CoastLink | null {
  if (typeof value !== "string") return null;
  const link = value.trim();
  if (!link || link.length > 2_048 || /\s/.test(link)) return null;
  if (link.startsWith("coast:")) return partsOf(link.slice("coast:".length));
  if (link.startsWith("/c/")) return partsOf(link.slice(3));
  if (/^https?:\/\//i.test(link)) {
    let url: URL;
    try {
      url = new URL(link);
    } catch {
      return null;
    }
    const origins = [...COAST_ORIGINS, ...(context.origins ?? []).map(originOf)];
    if (!origins.includes(url.origin) || !url.pathname.startsWith("/c/") || url.search) return null;
    return partsOf(`${url.pathname.slice(3)}${url.hash}`);
  }
  // `<view>#<section>`, as a chat kept a post's link before, in that chat's context.
  if (context.chat && /^[A-Za-z0-9_-]{1,64}(#|$)/.test(link)) return partsOf(`${context.chat}/${link}`);
  return null;
}

/** A link in the one form Coast keeps and hands out: `coast:<chat>[/<view>][#<section>[?<params>]]`. */
export function writeLink(link: CoastLink): string {
  const query = link.section && link.params ? new URLSearchParams(link.params).toString() : "";
  const section = link.view && link.section ? `#${link.section}${query ? `?${query}` : ""}` : "";
  return `coast:${link.chat}${link.view ? `/${link.view}` : ""}${section}`;
}

/** A link read in any form `parseLink` takes and written in the `coast:` form, or null for one it doesn't. */
export function normalLink(value: unknown, context: LinkContext = {}): string | null {
  const parsed = parseLink(value, context);
  return parsed ? writeLink(parsed) : null;
}

/** What a tool says of a link it can't read. */
export const LINK_PROBLEM =
  "A link is Coast's, coast:<chat>/<view>#<section>?<key>=<value>, such as coast:k49csx5a/design#suggestion?id=sg_7.";
